import React, { useState } from "react";
import { X, ArrowRight, ArrowLeft, Video, Sun, Ruler, CheckCircle2 } from "lucide-react";

/** 360° walkaround videó rövid útmutató (nem külön szög-fotók). */
const STORAGE_KEY = "autopark_viewer360_onboarding_v2";

const STEPS = [
  {
    title: "Előkészítés",
    desc: "Tiszta, jól megvilágított hely — parkoló vagy udvar. 1–2 méter távolság az autótól, lassú tempóban sétálj körbe.",
    Icon: Sun,
  },
  {
    title: "Videó felvétel az appban",
    desc: "A hirdetés űrlapján nyisd meg a kamerát — széles lencse alapból. Egy teljes kör ~25 mp körbejárás az autó körül, utána leállítás.",
    Icon: Video,
  },
  {
    title: "Feldolgozás",
    desc: "A rendszer stabilizálja a felvételt, kivágja a kockákat. A gyorsnézetben és az adatlapon kurzorral lapozhatod.",
    Icon: Ruler,
  },
];

export function PremiumOnboarding() {
  const [open, setOpen] = useState(() => {
    if (typeof window === "undefined") return false;
    try { return localStorage.getItem(STORAGE_KEY) !== "1"; } catch (_) { return true; }
  });
  const [step, setStep] = useState(0);

  const close = () => {
    try { localStorage.setItem(STORAGE_KEY, "1"); } catch (_) {}
    setOpen(false);
  };

  const skip = () => close();
  const next = () => setStep((s) => Math.min(STEPS.length, s + 1));
  const prev = () => setStep((s) => Math.max(0, s - 1));

  if (!open) return null;

  const isSummary = step === STEPS.length;
  const current = STEPS[step];

  return (
    <div data-testid="viewer360-onboarding" className="fixed inset-0 z-50 bg-black/75 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="w-full max-w-lg bg-anthracite-800 border border-white/10 rounded-3xl p-6 relative">
        <button data-testid="onboarding-close" onClick={close} className="absolute top-3 right-3 p-2 rounded-full hover:bg-white/10">
          <X className="w-4 h-4" />
        </button>

        <div className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-electric mb-2">
          360° videó útmutató · {isSummary ? "Kész" : `${step + 1} / ${STEPS.length}`}
        </div>

        {!isSummary ? (
          <div className="flex items-start gap-4">
            <div className="w-14 h-14 rounded-2xl bg-electric/15 flex items-center justify-center shrink-0">
              <current.Icon className="w-6 h-6 text-electric" strokeWidth={1.5} />
            </div>
            <div>
              <h3 className="font-heading text-2xl font-light">{current.title}</h3>
              <p className="text-sm text-slate-300 mt-2 leading-relaxed">{current.desc}</p>
            </div>
          </div>
        ) : (
          <div className="text-center py-6" data-testid="onboarding-summary">
            <div className="w-16 h-16 rounded-2xl bg-emerald-500/15 flex items-center justify-center mx-auto">
              <CheckCircle2 className="w-8 h-8 text-emerald-400" strokeWidth={1.5} />
            </div>
            <h3 className="font-heading text-2xl font-light mt-4">Mehet a felvétel!</h3>
            <p className="text-sm text-slate-400 mt-2 max-w-sm mx-auto">
              Egy walkaround videó elég — nem kell 9 külön fotó. Feldolgozás után a 360° nézet automatikusan megjelenik a hirdetésen.
            </p>
          </div>
        )}

        <div className="flex items-center justify-between mt-8">
          <button data-testid="onboarding-skip" onClick={skip} className="text-xs text-slate-400 hover:text-white">
            Kihagyás
          </button>
          <div className="flex items-center gap-2">
            {step > 0 && !isSummary && (
              <button data-testid="onboarding-prev" onClick={prev} className="inline-flex items-center gap-1 px-4 py-2 rounded-full glass hover:bg-white/10 text-sm">
                <ArrowLeft className="w-3.5 h-3.5" /> Vissza
              </button>
            )}
            {!isSummary ? (
              <button data-testid="onboarding-next" onClick={next} className="inline-flex items-center gap-1 px-5 py-2 rounded-full bg-electric hover:bg-electric-light text-white text-sm">
                Tovább <ArrowRight className="w-3.5 h-3.5" />
              </button>
            ) : (
              <button data-testid="onboarding-done" onClick={close} className="inline-flex items-center gap-1 px-5 py-2 rounded-full bg-electric hover:bg-electric-light text-white text-sm">
                Kezdés
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
